<template>
  <Form as="div" ref="form" class="view-edit-order-details" :validation-schema="validationSchema">
    <div class="form-cnt">
      <div class="form flex order-details">
        <BackstageInput
          :read-only-value="orderDetails.orderNumber"
          :label="$t('viewOrder.sales_order_number')"
          :read-only="true"></BackstageInput>
        <BackstageInputCalendar
          name="ship_date"
          v-if="isExistingOrder"
          v-model="orderDetails.dueDate"
          :label="$t('viewOrder.ship_date')"
          :disabled-dates="disabledDates"
          :read-only="!canEditDate"></BackstageInputCalendar>
        <div
          v-if="contacts && canEditOrder && !isExistingOrder"
          class="field-new"
          :class="[{ 'read-only': !canEditOrder || hasContactId }]">
          <label>{{ $t('viewOrder.contact') }}</label>
          <select v-model="orderDetails.contactId" :class="[{ 'with-arrow': canEditOrder }]" :disabled="!canEditOrder">
            <option v-for="contact in contacts" :key="contact.id" :value="contact.id">
              {{ `${contact.firstName} ${contact.lastName}${hasBuyerFunction(contact) ? ' (Buyer)' : ''}` }}
            </option>
          </select>
        </div>
        <BackstageInput
          v-if="!isPoNumberDisabled"
          name="customer_po_number"
          v-model="orderDetails.customerPoNumber"
          :label="$t('viewOrder.customer_po_number')"
          data-cy-input="customer-po-number-editable"></BackstageInput>
        <BackstageInput
          v-else-if="orderDetails.customerPoNumber"
          :read-only-value="orderDetails.customerPoNumber"
          :label="$t('viewOrder.customer_po_number')"
          :read-only="true"
          data-cy-input="customer-po-number-read-only"></BackstageInput>
        <BackstageInput
          :read-only-value="formatDateNoUTC(orderDetails.date)"
          :label="$t('viewOrder.order_date')"
          :read-only="true"></BackstageInput>
        <BackstageInput
          :read-only-value="getOrderTypeText()"
          :label="$t('viewOrder.sales_order_type')"
          :read-only="true"></BackstageInput>
        <BackstageInput
          :class="{ hold: isHoldOrder }"
          :read-only-value="$t(`viewOrders.order_status['${orderDetails.status.toLocaleLowerCase()}']`)"
          :label="$t('viewOrder.status')"
          :read-only="true"></BackstageInput>
        <div v-if="orderDetails.trackingNumbers && orderDetails.trackingNumbers.length > 0" class="field">
          <label>{{ $t('viewOrder.tracking_codes') }}</label>
          <span
            v-for="trackingObject in orderDetails.trackingNumbers"
            :key="trackingObject.trackingNumber"
            class="tracking-object">
            {{ trackingObject.trackingNumber }}
          </span>
        </div>
      </div>
      <div v-if="canEditAddress" class="address-container">
        <OneTimeAddress
          :is-enabled="true"
          :account-address="orderDetails.deliveryAddress"
          :title="t('oneTimeAddress.default.title')"
          :button-label="t('oneTimeAddress.default.btn_label')"
          v-on:delivery-address-updated="updateDeliveryAddress">
        </OneTimeAddress>
      </div>
      <div v-if="showDisabledDropshipNote" class="order-note">
        {{ $t('viewOrder.dropship_disabled_for_account_note') }}
      </div>
      <div v-if="isExistingOrder && hasDocuments(orderDetails, $t, userLocale)" class="order-documents">
        <BackstageInput
          v-if="orderDetails.documentStatus"
          :class="{ hold: isHoldOrder }"
          :read-only-value="orderDetails.documentStatus"
          :label="$t('viewOrder.sales_order_document_status')"
          :read-only="true"></BackstageInput>
        <grid-order-document :v-if="orderDetails" :order="orderDetails" />
      </div>
    </div>
  </Form>
</template>

<script lang="ts">
import BackstageInput from '@/components/common/BackstageInput.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import OneTimeAddress from '@/components/common/OneTimeAddress.vue';
import { useOrderValidation } from '@/composables/useOrderValidation';
import { OrderEditType } from '@/constants/orderEditType';
import { localeMixins } from '@/mixins/locale.mixins';
import type { AccountAddress } from '@/models/account-address.model';
import { ButtonVariant } from '@/models/button-variant.enum';
import { DraftInitInfoDto, OrderStatus, OrderType, OrderTypeDto } from '@onrunning/backstage-models';
import structuredClone from '@ungap/structured-clone';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { Form } from 'vee-validate';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { type AnyObject, object, string } from 'yup';
import { useFormat } from '../../composables/format';
import { useLocale } from '../../composables/locale';
import { toRawDeep } from '../../composables/to-raw-deep';
import { countriesWithStates, globalConstants, type State } from '../../constants/global.constants';
import { ordersMixins } from '../../mixins/orders.mixins';
import { Address } from '../../models/address.model';
import { Feature } from '../../models/feature.model';
import { Order } from '../../models/order.model';
import { SFContactModel } from '../../models/sf-contact.model';
import { DraftsService } from '../../services/drafts.service';
import { FeaturesService } from '../../services/features.service';
import { useUserStore } from '../../stores/user';
import gridOrderDocument from './grid-order-document.vue';

const defaultAddressValue = 'default';
const oneTimeAddressValue = 'oneTimeAddress';

export default defineComponent({
  name: 'view-edit-order-details',
  components: {
    gridOrderDocument,
    // eslint-disable-next-line vue/no-reserved-component-names
    Form,
    BackstageInput,
    BackstageInputCalendar,
    OneTimeAddress,
  },
  props: {
    order: {
      type: Object,
      default: undefined,
    },
    editType: {
      type: String as () => OrderEditType,
      default: undefined,
    },
    maximumDate: {
      type: Date,
      default: undefined,
    },
  },
  emits: ['order-valid', 'order-updated'],
  setup({ editType }) {
    const { selectedAccount } = storeToRefs(useUserStore());
    const { hasBuyerFunction, isASalesRep, isADealer, userProfile } = useUserStore();
    const { userLocale } = useLocale();
    const { formatDateNoUTC } = useFormat();
    const { isOrderEditMode } = useOrderValidation(editType);
    const { t } = useI18n();

    return {
      isOrderEditMode,
      account: selectedAccount,
      userLocale,
      ButtonVariant,
      hasBuyerFunction,
      isASalesRep,
      isADealer,
      userProfile,
      formatDateNoUTC,
      t,
    };
  },
  data() {
    const orderDetails = structuredClone(toRawDeep(this.order)) as Order;
    const selectedAddress = orderDetails.deliveryAddress?.name ? oneTimeAddressValue : defaultAddressValue;

    return {
      dropshipEnabled: false,
      editAddressButtonToggled: false,
      contacts: undefined as SFContactModel[] | undefined,
      disabledDates: undefined as any,
      orderDetails,
      featuresService: new FeaturesService(),
      draftsService: new DraftsService(),
      poNumberMaxChars: 20,
      selectedAddress,
      hasContactId: false,
    };
  },
  computed: {
    isPoNumberDisabled(): boolean {
      return !this.canEditOrder;
    },
    isExistingOrder(): boolean {
      return !this.theOrderIsDraft(this.orderDetails);
    },
    canEditOrder(): boolean {
      if (this.theOrderIsDraft(this.orderDetails)) {
        return true;
      }

      if (!this.isOrderEditMode) {
        return false;
      }

      const isSalesRepWithPermission = this.isASalesRep && this.isOrderEditableForSalesReps(this.orderDetails);

      const isDealerWithPermission =
        this.isADealer && this.isOrderEditableForDealer(this.orderDetails, this.userProfile?.user);

      return isSalesRepWithPermission || isDealerWithPermission;
    },
    canEditAddress(): boolean {
      return (
        this.theOrderIsDraft(this.orderDetails) &&
        this.dropshipEnabled &&
        this.orderDetails.type.toLocaleLowerCase() === OrderType.ReOrder.toLocaleLowerCase()
      );
    },
    canEditDate(): boolean {
      return this.theOrderIsDraft(this.orderDetails);
    },
    showEditAddressForm(): boolean {
      return this.canEditAddress && this.editAddressButtonToggled && !!this.orderDetails?.deliveryAddress;
    },
    oneTimeAddress(): string | undefined {
      return this.canEditAddress &&
        !!this.orderDetails?.deliveryAddress?.city &&
        !!this.orderDetails?.deliveryAddress?.zipCode &&
        !!this.orderDetails?.deliveryAddress?.street &&
        !!this.orderDetails?.deliveryAddress?.name
        ? `${this.orderDetails?.deliveryAddress?.name} <br/>${
            this.orderDetails?.deliveryAddress?.street
          }, ${this.orderDetails?.deliveryAddress?.zipCode}<br/>${
            this.orderDetails?.deliveryAddress?.state ? this.orderDetails?.deliveryAddress?.state + ' - ' : ''
          }${this.orderDetails?.deliveryAddress?.city} - ${this.orderDetails?.deliveryAddress?.country}`
        : undefined;
    },
    showDisabledDropshipNote(): boolean {
      return (
        !this.dropshipEnabled &&
        this.canEditOrder &&
        this.orderDetails?.type.toLocaleLowerCase() === OrderType.ReOrder.toLocaleLowerCase()
      );
    },
    isHoldOrder(): boolean {
      return this.orderDetails?.status === OrderStatus.Hold;
    },
    isReOrder(): boolean {
      return this.orderDetails.type.toLocaleLowerCase() === OrderType.ReOrder.toLocaleLowerCase();
    },
    validationSchema(): AnyObject {
      return object({
        customer_po_number: string().max(
          20,
          this.$t('formsValidations.rules_error_messages.max_chars', {
            field: this.$t('formsValidations.fields.customer_po_number'),
            max_chars: 20,
          }),
        ),
      });
    },
  },
  watch: {
    'orderDetails.customerPoNumber': {
      async handler() {
        const result = (await (this.$refs as any).form?.validate()) as any;
        this.$emit('order-valid', result.valid);
      },
    },
    orderDetails: {
      handler(newValue) {
        const customerPoNumberlength = this.orderDetails.customerPoNumber?.length ?? 0;

        if (customerPoNumberlength <= this.poNumberMaxChars) {
          this.$emit('order-updated', newValue);
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    ...ordersMixins,
    async init() {
      await this.setDropshipEnabled();
      this.hasContactId = this.contacts?.map((c) => c.id).includes(this.orderDetails?.contactId ?? '') ?? false;
      const draftInitInfoResult = await this.draftsService.getInitInfo(this.account.id);

      if (!draftInitInfoResult.isValid() || !draftInitInfoResult.model) {
        // show error
        return;
      }

      const selectedOrderType = draftInitInfoResult.model?.orderTypes.find(
        (orderType: OrderTypeDto) => orderType.type.toLocaleLowerCase() === this.orderDetails?.type.toLocaleLowerCase(),
      );

      this.disabledDates = (target: Date) => {
        const endDate = selectedOrderType?.endDate
          ? moment(selectedOrderType.endDate).toDate()
          : globalConstants.maximumDate;

        return target < moment(selectedOrderType?.startDate).toDate() || target > endDate;
      };

      if (this.isASalesRep) {
        this.loadContacts(draftInitInfoResult.model);
      }
    },
    async setDropshipEnabled() {
      this.dropshipEnabled = !(await this.featuresService.hasFeature(
        Feature.DROPSHIP_ORDERS_DISABLED,
        this.account.id,
      ));
    },
    async loadContacts(draftInitInfo: DraftInitInfoDto) {
      this.contacts = draftInitInfo.userContacts;
    },
    states(): State[] {
      const selectedCountry = this.account?.billingCountry?.toLowerCase();

      if (!Object.keys(countriesWithStates).includes(selectedCountry)) {
        return [];
      }

      return countriesWithStates[selectedCountry as keyof typeof countriesWithStates];
    },
    toggleEditAddress() {
      if (!this.editAddressButtonToggled && !this.orderDetails.deliveryAddress?.name) {
        this.orderDetails.deliveryAddress = new Address();
        this.orderDetails.deliveryAddress.country = this.account?.billingCountry;
      }

      this.editAddressButtonToggled = !this.editAddressButtonToggled;
    },
    updateDeliveryAddress(deliveryAddress?: AccountAddress | Address) {
      this.orderDetails.deliveryAddress = deliveryAddress as Address;
      this.selectedAddress = oneTimeAddressValue;
      this.$emit('order-updated', this.orderDetails);
    },
    removeDeliveryAddress() {
      this.orderDetails.deliveryAddress = undefined;
      this.selectedAddress = defaultAddressValue;
      this.$emit('order-updated', this.orderDetails);
    },
    getOrderTypeText(): string {
      const title = localeMixins.getLocaleTextByOrderType('viewOrders.order_type.', this.orderDetails.type);

      return this.$t(title);
    },
  },
});
</script>
<style scoped lang="scss">
.view-edit-order-details {
  .form-cnt {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    flex-wrap: wrap;

    .form {
      &.flex {
        display: grid;
        grid-template-columns: repeat(auto-fill, 250px);
        grid-auto-flow: row;
        align-items: start;
        justify-content: space-between;
        grid-gap: 20px;
        row-gap: 20px;
        flex: 1;

        .field,
        .field-new {
          width: 250px;
        }
      }
    }

    .order-details {
      @include container-background;
      margin-top: 0;
      align-items: flex-start;
    }

    .address-container {
      width: 400px;
      padding: 1.25rem;
      align-self: flex-start;
      border-radius: 0.25rem;
      background-color: var(--primary-bg-color);
    }

    .order-documents:deep() {
      @include container-background;
      width: 400px;
      justify-content: center;
      margin: 0;

      .title {
        font-size: 12px;
        text-align: left;
        margin: 0 0 12px;
        font-weight: 400;
        color: var(--primary-label-input-color);
      }

      .or {
        width: 100%;
        height: 1px;
        background-color: var(--border-inactive-color);
        position: relative;
        margin: 21px 0;

        span {
          display: block;
          padding: 0 10px;
          background: var(--primary-bg-color);
          color: var(--primary-text-remark-color);
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 12px;
        }
      }

      .radio-new {
        width: 100%;

        i.ico {
          width: 24px;
          height: 24px;
          cursor: pointer;
        }
      }

      .button-container {
        width: 100%;

        button {
          width: 100%;
          justify-content: center;
        }
      }
    }

    .tracking-object {
      margin-right: 5px;
    }

    .hold {
      color: var(--special-red-color-darker);
    }

    .order-note {
      padding-top: 30px;
      border-top: 1px var(--gray-color)-border solid;
    }
  }
  select {
    &:disabled {
      color: var(--primary-text-input-color);
    }
  }
}
</style>
